import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/PurchaseHistory.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_ICONS = {
  subscription: 'fa-crown',
  variant: 'fa-chess',
  duke_skin: 'fa-shield-halved',
};

const TYPE_LABELS = {
  subscription: 'Subscription',
  variant: 'Game Variant',
  duke_skin: 'Duke Skin',
};

const STATUS_COLORS = {
  paid: '#22c55e',
  active: '#3b82f6',
  pending: '#eab308',
  failed: '#ef4444',
  expired: '#6b7280',
};

const formatAmount = (amount, currency = 'usd') => {
  const dollars = typeof amount === 'number' && amount > 50 ? amount / 100 : amount;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(dollars);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PurchaseHistory = ({ onClose }) => {
  const { token } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPurchaseHistory();
  }, []);

  const fetchPurchaseHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const authToken = token;
      const res = await fetch(`${API}/purchase-history`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch purchase history');
      const data = await res.json();
      setPurchases(data.purchases || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="purchase-history-overlay" data-testid="purchase-history-overlay" onClick={onClose}>
      <div className="purchase-history-modal" data-testid="purchase-history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ph-header">
          <h2 data-testid="purchase-history-title">
            <i className="fa-solid fa-receipt"></i>
            Purchase History
          </h2>
          <button className="ph-close-btn" data-testid="purchase-history-close-btn" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className="ph-body">
          {loading ? (
            <div className="ph-loading" data-testid="purchase-history-loading">
              <div className="ph-spinner"></div>
              <p>Loading purchases...</p>
            </div>
          ) : error ? (
            <div className="ph-error" data-testid="purchase-history-error">
              <i className="fa-solid fa-triangle-exclamation"></i>
              <p>{error}</p>
              <button onClick={fetchPurchaseHistory} data-testid="purchase-history-retry-btn">Retry</button>
            </div>
          ) : purchases.length === 0 ? (
            <div className="ph-empty" data-testid="purchase-history-empty">
              <i className="fa-solid fa-bag-shopping"></i>
              <h3>No Purchases Yet</h3>
              <p>Your purchase history will appear here once you make a purchase or subscribe.</p>
            </div>
          ) : (
            <div className="ph-list" data-testid="purchase-history-list">
              {purchases.map((purchase, idx) => (
                <div key={purchase.id || purchase.label + purchase.date} className="ph-item" data-testid={`purchase-item-${purchase.id}`}>
                  <div className="ph-item-icon">
                    <i className={`fa-solid ${TYPE_ICONS[purchase.type] || 'fa-cart-shopping'}`}></i>
                  </div>
                  <div className="ph-item-details">
                    <div className="ph-item-label">{purchase.label}</div>
                    <div className="ph-item-meta">
                      <span className="ph-item-type">{TYPE_LABELS[purchase.type] || purchase.type}</span>
                      <span className="ph-item-date">{formatDate(purchase.date)}</span>
                    </div>
                  </div>
                  <div className="ph-item-right">
                    <div className="ph-item-amount">{formatAmount(purchase.amount, purchase.currency)}</div>
                    <div
                      className="ph-item-status"
                      style={{ color: STATUS_COLORS[purchase.status] || '#6b7280' }}
                      data-testid={`purchase-status-${idx}`}
                    >
                      {purchase.status === 'paid' || purchase.status === 'active' ? (
                        <i className="fa-solid fa-circle-check"></i>
                      ) : purchase.status === 'pending' ? (
                        <i className="fa-solid fa-clock"></i>
                      ) : (
                        <i className="fa-solid fa-circle-xmark"></i>
                      )}
                      {' '}{purchase.status.charAt(0).toUpperCase() + purchase.status.slice(1)}
                    </div>
                    {purchase.invoice_url && (
                      <a
                        href={purchase.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ph-invoice-link"
                        data-testid={`purchase-invoice-${idx}`}
                      >
                        <i className="fa-solid fa-file-invoice"></i> Invoice
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseHistory;
